import { noop } from 'lodash';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { entityDefinition } from '@work4all/models/lib/Classes/entityDefinitions';
import { ERPTypes } from '@work4all/models/lib/Classes/ERPTypes.entity';
import { FieldDefinitions } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { settings, useSetting } from '../../../../../../settings';

type ContextValue = {
  canEditAutomaticNumberingField: boolean;
  isAutomaticNumberingEnabled: boolean;
  isRecordAutomaticNumberingEnabled: boolean;
  isUserAutomaticNumberingEnabled: boolean;
  toggleUserAutomaticNumbering: () => void;
  toggleRecordAutomaticNumbering: () => void;
  deletePositionsNumber: () => void;
};

type ProviderProps = {
  entity: Entities;
  isRecordAutomaticNumberingEnabled: boolean;
  toggleRecordAutomaticNumbering: () => void;
  deletePositionsNumber: () => void;
};

const ErpPositionsAutomaticNumberingContext = createContext<ContextValue>({
  canEditAutomaticNumberingField: false,
  isAutomaticNumberingEnabled: false,
  isRecordAutomaticNumberingEnabled: false,
  isUserAutomaticNumberingEnabled: false,
  toggleUserAutomaticNumbering: noop,
  toggleRecordAutomaticNumbering: noop,
  deletePositionsNumber: noop,
});

export const ErpPositionsAutomaticNumberingProvider = ({
  entity,
  children,
  isRecordAutomaticNumberingEnabled,
  toggleRecordAutomaticNumbering,
  deletePositionsNumber,
}: PropsWithChildren<ProviderProps>) => {
  const fields = entityDefinition[entity]
    .fieldDefinitions as FieldDefinitions<ERPTypes>;

  const isAutomaticNumberingFieldAvailable = Boolean(
    fields?.automaticNumbering
  );

  const automaticNumberingSetting = useSetting(
    settings.erpPositionsAutomaticNumbering()
  );

  const value: ContextValue = useMemo(() => {
    const isUserAutomaticNumberingEnabled =
      automaticNumberingSetting.value === -1;

    return {
      canEditAutomaticNumberingField: isAutomaticNumberingFieldAvailable,
      isAutomaticNumberingEnabled:
        isUserAutomaticNumberingEnabled || isRecordAutomaticNumberingEnabled,
      isRecordAutomaticNumberingEnabled,
      isUserAutomaticNumberingEnabled,
      toggleUserAutomaticNumbering: () =>
        automaticNumberingSetting.set(isUserAutomaticNumberingEnabled ? 0 : -1),
      toggleRecordAutomaticNumbering,
      deletePositionsNumber,
    };
  }, [
    automaticNumberingSetting,
    isAutomaticNumberingFieldAvailable,
    isRecordAutomaticNumberingEnabled,
    toggleRecordAutomaticNumbering,
    deletePositionsNumber,
  ]);

  return (
    <ErpPositionsAutomaticNumberingContext.Provider value={value}>
      {children}
    </ErpPositionsAutomaticNumberingContext.Provider>
  );
};

export const useErpPositionsAutomaticNumberingContext = () =>
  useContext(ErpPositionsAutomaticNumberingContext);
